import React, { useState, useEffect, useRef } from "react";
import { Send, Volume2, Loader2 } from "lucide-react";
import agentWorkflowService from "../api";
import io from "socket.io-client";
import { buildApiUrl } from "../../../services/apiConfig";
import VoiceSphere from "../../wealthChatbot/components/VoiceSphere";
import wealthChatbotService from "../../wealthChatbot/api";

const styleText = (text) => {
    if (!text) return text;
    return text
        .replace(/#### (.*?)(\n|$)/g, "<h4 class='text-base font-semibold my-2'>$1</h4>")
        .replace(/### (.*?)(\n|$)/g, "<h3 class='text-lg font-semibold my-2'>$1</h3>")
        .replace(/## (.*?)(\n|$)/g, "<h2 class='text-xl font-semibold my-2'>$1</h2>")
        .replace(/# (.*?)(\n|$)/g, "<h1 class='text-2xl font-bold my-2'>$1</h1>")
        .replace(/\*\*(.*?)\*\*/g, "<b>$1</b>")
        .replace(/\*(.*?)\*/g, "<i>$1</i>")
        .replace(/\n/g, "<br>");
};

const downsampleBuffer = (buffer, originalSampleRate, targetSampleRate) => {
    if (originalSampleRate === targetSampleRate) return buffer;
    const ratio = originalSampleRate / targetSampleRate;
    const newLength = Math.round(buffer.length / ratio);
    const result = new Float32Array(newLength);
    for (let i = 0; i < newLength; i++) {
        result[i] = buffer[Math.floor(i * ratio)];
    }
    return result;
};

const floatTo16BitPCM = (input) => {
    const output = new Int16Array(input.length);
    for (let i = 0; i < input.length; i++) {
        const s = Math.max(-1, Math.min(1, input[i]));
        output[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }
    return output.buffer;
};

const ChatBox = ({ onWorkflowUpdate, onQueryStart, useNovaV2, onToggleV2 }) => {
    const [messages, setMessages] = useState([
        {
            text: "Hello! I'm your assistant. Ask me something to see how I process your request.",
            sender: "bot"
        }
    ]);
    const [input, setInput] = useState("");
    const [isBotTyping, setIsBotTyping] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [userId, setUserId] = useState(null);

    const [isVoiceActive, setIsVoiceActive] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [voiceTranscription, setVoiceTranscription] = useState("");

    const messagesEndRef = useRef(null);
    const runIdRef = useRef(null);
    const pendingApprovalRunIdRef = useRef(null); // run_id of an interrupt awaiting a reply
    const socketRef = useRef(null);
    const audioContextRef = useRef(null);
    const mediaStreamRef = useRef(null);
    const processorRef = useRef(null);
    const transcriptionTextRef = useRef("");
    const confirmedTranscriptRef = useRef("");
    const silenceTimerRef = useRef(null);
    const audioRef = useRef(null);
    const isVoiceBusyRef = useRef(false);
    const isVoiceActiveRef = useRef(false);

    useEffect(() => {
        const storedUserId = sessionStorage.getItem('user_id');
        setUserId(storedUserId || 'U1757535255');
    }, []);

    useEffect(() => {
        if (useNovaV2) {
            agentWorkflowService.getSuggestions().then(setSuggestions);
        } else {
            setSuggestions([]);
        }
    }, [useNovaV2]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    useEffect(() => {
        if (useNovaV2) {
            wealthChatbotService.getSuggestions().then(setSuggestions);
        } else {
            setSuggestions([]);
        }
    }, [useNovaV2]);

    useEffect(() => {
        if (!socketRef.current) {
            const socketUrl = buildApiUrl('SOCKET');
            socketRef.current = io(socketUrl, {
                path: '/socket.io/',
                transports: ["websocket", "polling"],
                reconnectionAttempts: 5
            });

            const socket = socketRef.current;
            socket.on("transcription", (data) => {
                if (!data || !data.text) return;
                const rawText = data.text;
                if (rawText.startsWith("##")) {
                    const finalizedSentence = rawText.replace(/^##\s*/, '').trim();
                    const prefix = confirmedTranscriptRef.current ? confirmedTranscriptRef.current + " " : "";
                    confirmedTranscriptRef.current = prefix + finalizedSentence;
                    transcriptionTextRef.current = confirmedTranscriptRef.current;
                    setVoiceTranscription(confirmedTranscriptRef.current);
                } else if (rawText.startsWith(">>")) {
                    const interim = rawText.replace(/^>>\s*/, '').trim();
                    const combined = confirmedTranscriptRef.current
                        ? confirmedTranscriptRef.current + " " + interim
                        : interim;
                    transcriptionTextRef.current = combined;
                    setVoiceTranscription(combined);
                } else {
                    const prefix = confirmedTranscriptRef.current ? confirmedTranscriptRef.current + " " : "";
                    transcriptionTextRef.current = prefix + rawText.trim();
                    setVoiceTranscription(transcriptionTextRef.current);
                }
            });
        }
        return () => {
            if (socketRef.current) {
                socketRef.current.disconnect();
                socketRef.current = null;
            }
        };
    }, []);

    const handleStartRecording = async () => {
        if (isListening) return false;
        setVoiceTranscription("");
        transcriptionTextRef.current = "";
        confirmedTranscriptRef.current = "";
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaStreamRef.current = stream;
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            const audioContext = new AudioContext();
            audioContextRef.current = audioContext;
            try {
                await audioContext.audioWorklet.addModule('/audio-processor.js');
            } catch (error) {
                console.error("Failed to load audio worklet:", error);
                throw new Error("Failed to initialize audio processing");
            }
            const source = audioContext.createMediaStreamSource(stream);
            const workletNode = new AudioWorkletNode(audioContext, 'audio-processor');
            workletNode.port.onmessage = (event) => {
                try {
                    if (event.data.audioData) {
                        const inputData = event.data.audioData;
                        const downsampled = downsampleBuffer(inputData, audioContext.sampleRate, 16000);
                        const pcmBuffer = floatTo16BitPCM(downsampled);
                        if (socketRef.current && socketRef.current.connected) {
                            socketRef.current.emit("audio_stream", new Uint8Array(pcmBuffer));
                        }
                    }
                } catch (err) {
                    console.error("Error processing audio:", err);
                }
            };
            source.connect(workletNode);
            workletNode.connect(audioContext.destination);
            processorRef.current = workletNode;
            return true;
        } catch (error) {
            console.error("Error starting recording:", error);
            return false;
        }
    };

    const handleStopRecording = async () => {
        try {
            if (socketRef.current) socketRef.current.emit("stop_audio");
            if (processorRef.current) {
                processorRef.current.port.postMessage({ terminate: true });
                processorRef.current.disconnect();
                processorRef.current = null;
            }
            if (mediaStreamRef.current) {
                mediaStreamRef.current.getTracks().forEach(t => t.stop());
                mediaStreamRef.current = null;
            }
            if (audioContextRef.current) {
                await audioContextRef.current.close();
                audioContextRef.current = null;
            }
        } catch (error) {
            console.error("Error stopping recording:", error);
        }
        return transcriptionTextRef.current;
    };

    const startVoiceInteraction = async () => {
        if (isVoiceBusyRef.current) return;
        if (silenceTimerRef.current) {
            clearInterval(silenceTimerRef.current);
            silenceTimerRef.current = null;
        }
        isVoiceActiveRef.current = true;
        transcriptionTextRef.current = "";
        setIsVoiceActive(true);
        setVoiceTranscription("");

        const success = await handleStartRecording();
        if (success) {
            setIsListening(true);
            let lastTranscription = transcriptionTextRef.current;
            let silenceCounter = 0;
            const checkSilence = () => {
                if (!silenceTimerRef.current) return;
                if (transcriptionTextRef.current === lastTranscription) {
                    silenceCounter++;
                    if (silenceCounter >= 6 && transcriptionTextRef.current.trim().length > 0) {
                        clearInterval(silenceTimerRef.current);
                        silenceTimerRef.current = null;
                        stopVoiceInteraction();
                    }
                } else {
                    silenceCounter = 0;
                    lastTranscription = transcriptionTextRef.current;
                }
            };
            silenceTimerRef.current = setInterval(checkSilence, 500);
        } else {
            isVoiceActiveRef.current = false;
            setIsVoiceActive(false);
            alert("Could not access microphone. Please check your permissions and try again.");
        }
    };

    const stopVoiceInteraction = async () => {
        if (isVoiceBusyRef.current) return;
        isVoiceBusyRef.current = true;
        if (silenceTimerRef.current) {
            clearInterval(silenceTimerRef.current);
            silenceTimerRef.current = null;
        }
        setIsListening(false);
        setIsProcessing(true);
        await handleStopRecording();

        const finalTranscription = transcriptionTextRef.current.trim();
        setVoiceTranscription(finalTranscription);

        if (finalTranscription) {
            try {
                const custId = sessionStorage.getItem('cust_id') || 'CUST007';
                const userMessage = { text: finalTranscription, sender: "user" };
                setMessages(prev => [...prev, userMessage]);

                const response = useNovaV2
                    ? await agentWorkflowService.sendMessageV2(finalTranscription, custId, userId)
                    : await agentWorkflowService.sendMessage(finalTranscription, custId, userId);

                let accumulatedText = "";
                let ttsTriggered = false;

                const triggerTTS = async () => {
                    if (ttsTriggered || !accumulatedText.trim()) return;
                    ttsTriggered = true;
                    const cleanText = accumulatedText
                        .replace(/\*\*(.*?)\*\*/g, "$1")
                        .replace(/\*(.*?)\*/g, "$1")
                        .replace(/<[^>]*>/g, "")
                        .replace(/#{1,6}\s+/g, "")
                        .replace(/\|.*?\|/g, "")
                        .replace(/[-:]+/g, "");
                    setIsProcessing(false);
                    setIsSpeaking(true);
                    const ttsResult = await wealthChatbotService.generateTextToSpeech(cleanText);
                    const restartListening = () => {
                        isVoiceBusyRef.current = false;
                        transcriptionTextRef.current = "";
                        confirmedTranscriptRef.current = "";
                        setVoiceTranscription("");
                        if (isVoiceActiveRef.current) startVoiceInteraction();
                    };
                    if (ttsResult.success) {
                        const audioUrl = URL.createObjectURL(ttsResult.audioBlob);
                        const audio = new Audio(audioUrl);
                        audioRef.current = audio;
                        audio.onended = () => {
                            audioRef.current = null;
                            setIsSpeaking(false);
                            URL.revokeObjectURL(audioUrl);
                            restartListening();
                        };
                        audio.play();
                    } else {
                        setIsSpeaking(false);
                        restartListening();
                    }
                };

                try {
                    const { createParser } = await import('eventsource-parser');
                    const parser = createParser({
                        onEvent: async (event) => {
                            const type = event.event;
                            if (type === "filler" || type === "interrupt" || type === "final") {
                                try {
                                    const parsedData = JSON.parse(event.data);
                                    if (parsedData.ERROR) throw new Error(parsedData.ERROR);
                                    if (type === "filler") {
                                        const fillerText = typeof parsedData.data === "string"
                                            ? parsedData.data
                                            : parsedData.data?.response || "";
                                        if (fillerText) {
                                            accumulatedText += fillerText;
                                            setMessages(prev => {
                                                const msgs = [...prev];
                                                const last = msgs[msgs.length - 1];
                                                if (last?.sender === "bot") {
                                                    msgs[msgs.length - 1] = { ...last, text: styleText(accumulatedText) };
                                                } else {
                                                    msgs.push({ text: styleText(accumulatedText), sender: "bot" });
                                                }
                                                return msgs;
                                            });
                                        }
                                        return;
                                    }
                                    const responseText = parsedData.data?.response || (typeof parsedData.data === 'string' ? parsedData.data : '');
                                    if (responseText) {
                                        accumulatedText += responseText;
                                        setMessages(prev => {
                                            const msgs = [...prev];
                                            const last = msgs[msgs.length - 1];
                                            if (last?.sender === "bot") {
                                                msgs[msgs.length - 1] = { ...last, text: styleText(accumulatedText) };
                                            } else {
                                                msgs.push({ text: styleText(accumulatedText), sender: "bot" });
                                            }
                                            return msgs;
                                        });
                                        if (parsedData.data?.run_id) runIdRef.current = parsedData.data.run_id;
                                        if (type === "final") await triggerTTS();
                                    }
                                } catch (err) {
                                    console.error("Error processing voice event:", err);
                                }
                            }
                        }
                    });
                    const reader = response.body.getReader();
                    const decoder = new TextDecoder();
                    while (true) {
                        const { value, done } = await reader.read();
                        if (done) break;
                        parser.feed(decoder.decode(value, { stream: true }));
                    }
                    if (!ttsTriggered) await triggerTTS();
                } catch (streamError) {
                    console.error("Voice stream error:", streamError);
                    setIsProcessing(false);
                    isVoiceBusyRef.current = false;
                }
            } catch (error) {
                console.error("Error processing voice input:", error);
                setMessages(prev => [...prev, { text: error.message || "Error connecting to server.", sender: "bot" }]);
                setIsProcessing(false);
                isVoiceBusyRef.current = false;
            }
        } else {
            setIsProcessing(false);
            isVoiceBusyRef.current = false;
        }
    };

    const closeVoiceInterface = () => {
        isVoiceActiveRef.current = false;
        isVoiceBusyRef.current = false;
        if (silenceTimerRef.current) {
            clearInterval(silenceTimerRef.current);
            silenceTimerRef.current = null;
        }
        if (audioRef.current) {
            audioRef.current.onended = null;
            audioRef.current.onerror = null;
            audioRef.current.pause();
            audioRef.current = null;
        }
        if (socketRef.current) socketRef.current.emit("stop_audio");
        if (processorRef.current) {
            processorRef.current.port.postMessage({ terminate: true });
            processorRef.current.disconnect();
            processorRef.current = null;
        }
        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(t => t.stop());
            mediaStreamRef.current = null;
        }
        if (audioContextRef.current) {
            audioContextRef.current.close().catch(() => {});
            audioContextRef.current = null;
        }
        transcriptionTextRef.current = "";
        confirmedTranscriptRef.current = "";
        setIsVoiceActive(false);
        setIsListening(false);
        setIsProcessing(false);
        setIsSpeaking(false);
        setVoiceTranscription("");
    };

    const handleSendMessage = async (textOverride = null, templateId = null) => {
        const messageText = textOverride ?? input;
        if (messageText.trim() === "" || isBotTyping) return;

        const userMessage = { text: messageText, sender: "user" };
        setMessages(prev => [...prev, userMessage]);
        setInput("");
        setIsBotTyping(true);
        runIdRef.current = null;
        onQueryStart?.();

        try {
            const custId = sessionStorage.getItem('cust_id') || 'CUST007';
            const response = useNovaV2
                ? await agentWorkflowService.sendMessageV2(messageText, custId, userId, templateId)
                : await agentWorkflowService.sendMessage(messageText, custId, userId, pendingApprovalRunIdRef.current);

            setMessages(prev => [...prev, { text: "", sender: "bot", isStreaming: true }]);

            let accumulatedText = "";
            const shownStatuses = new Set();
            try {
                const { createParser } = await import('eventsource-parser');
                const parser = createParser({
                    onEvent: (event) => {
                        const type = event.event;
                        if (type === "filler" || type === "interrupt" || type === "final") {
                            try {
                                const parsedData = JSON.parse(event.data);
                                if (parsedData.ERROR && typeof parsedData.ERROR === 'string') {
                                    throw new Error(parsedData.ERROR);
                                }

                                if (type === "filler") {
                                    const statusText = (parsedData.data?.response ?? (typeof parsedData.data === 'string' ? parsedData.data : '')).trim();
                                    if (statusText && !shownStatuses.has(statusText)) {
                                        shownStatuses.add(statusText);
                                        setMessages(prev => {
                                            const msgs = [...prev];
                                            msgs.splice(msgs.length - 1, 0, { text: statusText, sender: "bot", isStatus: true, isLoading: true });
                                            return msgs;
                                        });
                                    }
                                    return;
                                }

                                let responseText = "";
                                if (typeof parsedData.data === 'string') {
                                    responseText = parsedData.data;
                                } else if (parsedData.data?.response) {
                                    responseText = parsedData.data.response;
                                } else if (parsedData.data) {
                                    responseText = JSON.stringify(parsedData.data);
                                }
                                accumulatedText += responseText;

                                if (parsedData.data?.run_id) runIdRef.current = parsedData.data.run_id;

                                // Track whether we're awaiting a reply to an interrupt
                                if (type === "interrupt") {
                                    pendingApprovalRunIdRef.current = parsedData.data?.run_id || null;
                                } else if (type === "final") {
                                    pendingApprovalRunIdRef.current = null;
                                }

                                setMessages(prev => {
                                    const msgs = [...prev];
                                    msgs[msgs.length - 1] = {
                                        text: styleText(accumulatedText),
                                        sender: "bot",
                                        isStreaming: type !== "final" && type !== "interrupt",
                                    };
                                    return msgs;
                                });

                                if (type === "final" || type === "interrupt") {
                                    setIsBotTyping(false);
                                    setMessages(prev => prev.map(m => m.isStatus ? { ...m, isLoading: false } : m));
                                }
                            } catch (error) {
                                console.error("Error processing event data:", error);
                            }
                        }
                    }
                });

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                while (true) {
                    const { value, done } = await reader.read();
                    if (done) break;
                    parser.feed(decoder.decode(value, { stream: true }));
                }

                const graphData = useNovaV2
                    ? await agentWorkflowService.getAgentGraphRunV2(userId, runIdRef.current)
                    : await agentWorkflowService.getAgentGraph(userId, runIdRef.current);
                onWorkflowUpdate(graphData);

            } catch (streamError) {
                console.error("Stream processing error:", streamError);
                throw streamError;
            }
        } catch (error) {
            console.error("Error in handleSendMessage:", error);
            pendingApprovalRunIdRef.current = null;
            setMessages(prev => [...prev, { text: error.message || "Error connecting to server.", sender: "bot" }]);
        } finally {
            setIsBotTyping(false);
        }
    };

    return (
        <div className="relative flex flex-col h-[400px] rounded-xl overflow-hidden border border-gray-200"
            style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>

            {/* Header */}
            <div
                className="flex items-center justify-between px-4 shrink-0"
                style={{
                    height: '48px',
                    background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)',
                }}
            >
                <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.8)' }} />
                    <span className="font-semibold text-sm text-white tracking-wide select-none">Agent Chat</span>
                </div>
                <button
                    type="button"
                    onClick={onToggleV2}
                    title={useNovaV2 ? "Switch to original endpoint" : "Switch to v2 endpoint"}
                    className="px-2 py-0.5 text-xs font-semibold rounded-full border transition-colors"
                    style={useNovaV2
                        ? { backgroundColor: 'rgba(255,255,255,0.9)', color: 'var(--color-primary)', borderColor: 'rgba(255,255,255,0.9)' }
                        : { backgroundColor: 'transparent', color: 'rgba(255,255,255,0.6)', borderColor: 'rgba(255,255,255,0.4)' }}
                >
                    v2
                </button>
            </div>

            {/* Messages */}
            <div
                className="flex-1 overflow-y-auto flex flex-col"
                style={{ backgroundColor: '#F9FAFB', padding: '16px', gap: '0' }}
                role="log"
                aria-live="polite"
            >
                {messages.map((msg, index) => {
                    if (msg.isStreaming) return null;
                    const isUser = msg.sender === 'user';

                    if (msg.isStatus) {
                        return (
                            <div
                                key={index}
                                className="self-start animate-slideInLeft"
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    padding: '6px 12px', margin: '4px 0',
                                    borderRadius: '12px',
                                    backgroundColor: '#EFF6FF',
                                    border: '1px solid #BFDBFE',
                                    color: '#3B82F6',
                                    fontSize: '12px', fontWeight: 500,
                                    maxWidth: '80%',
                                }}
                            >
                                {msg.isLoading && <Loader2 size={13} className="animate-spin" style={{ flexShrink: 0 }} />}
                                <span>{msg.text}</span>
                            </div>
                        );
                    }

                    return (
                        <div
                            key={index}
                            className={`relative ${isUser ? 'self-end animate-slideInRight' : 'self-start animate-slideInLeft'}`}
                            style={{
                                maxWidth: '80%',
                                padding: '12px 16px',
                                margin: '8px 0',
                                borderRadius: '20px',
                                ...(isUser
                                    ? {
                                        background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)',
                                        color: 'white',
                                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                        borderBottomRightRadius: '4px',
                                    }
                                    : {
                                        backgroundColor: '#F3F4F6',
                                        color: '#1F2937',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                        borderBottomLeftRadius: '4px',
                                    }
                                ),
                            }}
                        >
                            <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
                                <span dangerouslySetInnerHTML={{ __html: msg.text }} />
                            </div>
                        </div>
                    );
                })}

                {isBotTyping && (
                    <div
                        className="self-start flex items-center gap-1.5 animate-slideInLeft"
                        style={{
                            maxWidth: '80%', padding: '12px 16px', margin: '8px 0',
                            borderRadius: '20px', borderBottomLeftRadius: '4px',
                            backgroundColor: '#F3F4F6',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                        }}
                        role="status"
                        aria-label="Assistant is typing"
                    >
                        {[0, 0.2, 0.4].map((delay, i) => (
                            <span
                                key={i}
                                className="animate-typingDots"
                                style={{
                                    display: 'inline-block', width: '7px', height: '7px',
                                    borderRadius: '50%', backgroundColor: 'var(--color-primary)',
                                    opacity: 0.4, animationDelay: `${delay}s`,
                                }}
                            />
                        ))}
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            {/* VoiceSphere overlay */}
            {isVoiceActive && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                    <VoiceSphere
                        isActive={isVoiceActive}
                        isListening={isListening}
                        isProcessing={isProcessing}
                        isSpeaking={isSpeaking}
                        transcription={voiceTranscription}
                        onClose={closeVoiceInterface}
                    />
                </div>
            )}

            {useNovaV2 && suggestions.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '10px 16px 4px', borderTop: '1px solid #E5E7EB', background: 'white' }}>
                    {suggestions.map(s => (
                        <button
                            key={s.id}
                            type="button"
                            onClick={() => {
                                setSuggestions(prev => prev.filter(x => x.id !== s.id));
                                handleSendMessage(s.query, s.id);
                            }}
                            disabled={isBotTyping}
                            title={s.description}
                            style={{
                                padding: '6px 12px', borderRadius: '16px',
                                border: '1px solid var(--color-secondary, #06B6D4)',
                                background: 'white', color: 'var(--color-primary, #0EA5E9)',
                                fontSize: '12px', fontWeight: 500,
                                cursor: isBotTyping ? 'not-allowed' : 'pointer',
                                opacity: isBotTyping ? 0.5 : 1,
                                transition: 'all 0.15s ease',
                                whiteSpace: 'nowrap', maxWidth: '200px',
                                overflow: 'hidden', textOverflow: 'ellipsis',
                            }}
                            onMouseEnter={e => { if (!isBotTyping) { e.currentTarget.style.background = 'var(--color-secondary, #06B6D4)'; e.currentTarget.style.color = 'white'; } }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = 'var(--color-primary, #0EA5E9)'; }}
                        >
                            {s.title}
                        </button>
                    ))}
                </div>
            )}

            {/* Input */}
            <div
                className="flex items-center gap-2 shrink-0"
                style={{ padding: '12px 16px', background: 'white', borderTop: '1px solid #E5E7EB' }}
            >
                <input
                    type="text"
                    placeholder="Type your message..."
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === 'Enter') { e.preventDefault(); handleSendMessage(); }
                    }}
                    disabled={isBotTyping}
                    style={{
                        flex: 1, fontSize: '14px', lineHeight: '1.4',
                        padding: '10px 14px', borderRadius: '12px',
                        border: '1px solid #D1D5DB', outline: 'none',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        backgroundColor: 'white', color: '#1F2937',
                    }}
                    onFocus={e => {
                        e.target.style.borderColor = 'var(--color-secondary)';
                        e.target.style.boxShadow = '0 0 0 3px rgba(6,182,212,0.1)';
                    }}
                    onBlur={e => {
                        e.target.style.borderColor = '#D1D5DB';
                        e.target.style.boxShadow = 'none';
                    }}
                />

                <button
                    type="button"
                    aria-label="Voice input"
                    onClick={() => isVoiceActive ? closeVoiceInterface() : startVoiceInteraction()}
                    disabled={isBotTyping}
                    style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: '36px', height: '36px', borderRadius: '8px',
                        border: 'none', background: 'transparent',
                        cursor: isBotTyping ? 'not-allowed' : 'pointer',
                        color: isVoiceActive ? 'var(--color-primary)' : isBotTyping ? '#D1D5DB' : '#6B7280',
                        transition: 'color 0.2s ease', outline: 'none',
                    }}
                    onMouseEnter={e => { if (!isBotTyping) e.currentTarget.style.color = 'var(--color-primary)'; }}
                    onMouseLeave={e => { if (!isBotTyping && !isVoiceActive) e.currentTarget.style.color = '#6B7280'; }}
                >
                    <Volume2 size={20} />
                </button>

                <button
                    type="button"
                    aria-label="Send message"
                    onClick={() => handleSendMessage()}
                    disabled={isBotTyping || input.trim() === ""}
                    style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: '10px 14px', borderRadius: '8px', border: 'none',
                        background: (isBotTyping || input.trim() === "")
                            ? '#E5E7EB'
                            : 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)',
                        color: (isBotTyping || input.trim() === "") ? '#9CA3AF' : 'white',
                        cursor: (isBotTyping || input.trim() === "") ? 'not-allowed' : 'pointer',
                        fontWeight: 600, fontSize: '14px',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        outline: 'none',
                        boxShadow: (isBotTyping || input.trim() === "") ? 'none' : '0 2px 6px rgba(0,0,0,0.12)',
                    }}
                    onMouseEnter={e => {
                        if (!isBotTyping && input.trim() !== "") {
                            e.currentTarget.style.transform = 'scale(1.05)';
                            e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.2)';
                        }
                    }}
                    onMouseLeave={e => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = (isBotTyping || input.trim() === "") ? 'none' : '0 2px 6px rgba(0,0,0,0.12)';
                    }}
                    onMouseDown={e => { if (!isBotTyping && input.trim() !== "") e.currentTarget.style.transform = 'scale(0.95)'; }}
                    onMouseUp={e => { if (!isBotTyping && input.trim() !== "") e.currentTarget.style.transform = 'scale(1.05)'; }}
                >
                    <Send size={16} />
                </button>
            </div>
        </div>
    );
};

export default ChatBox;
